import { useMemo, useState, type CSSProperties } from 'react';
import {
  Drawer,
  Timeline,
  Typography,
  Tag,
  Empty,
  Spin,
  Segmented,
  Button,
  Space,
  App,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { PartTimelineEntry, EcnChangeItem, SpecDocMeta } from '@bom/shared';
import { usePartTimeline, openEcnFile } from '../../api/ecns';
import { useSpecTimeline, openSpecFile } from '../../api/specs';
import { fmtTime } from '../../lib/format';

const { Text } = Typography;

interface Props {
  tid: string;
  partNo?: string | undefined;
  onClose: () => void;
}

function fmtDate(d?: string): string {
  if (!d) return '—';
  return d.length === 8 ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` : d;
}

interface Diff {
  label: string;
  before: string;
  after: string;
}

function diffFields(b: EcnChangeItem, a: EcnChangeItem): Diff[] {
  const rows: Diff[] = [];
  const push = (label: string, bv?: unknown, av?: unknown) => {
    const before = String(bv ?? '').trim();
    const after = String(av ?? '').trim();
    if (!before && !after) return;
    if (before === after) return;
    rows.push({ label, before, after });
  };
  push('料号', b.part_no, a.part_no);
  push('名称', b.name, a.name);
  push('规格', b.spec, a.spec);
  push('位号', b.designator, a.designator);
  push('用量', b.qty, a.qty);
  return rows;
}

const pillBase: CSSProperties = {
  padding: '1px 8px',
  borderRadius: 4,
  fontSize: 13,
  lineHeight: '20px',
  wordBreak: 'break-all',
};

export function PartTimelineDrawer({ tid, partNo, onClose }: Props) {
  const { message } = App.useApp();
  const [view, setView] = useState<'ecn' | 'spec'>('ecn');
  const q = usePartTimeline(tid, partNo);
  const specQ = useSpecTimeline(tid, partNo);

  // 同一 ECN 同一行的 before/after 两条反查项去重（按 ecn_id+line_no）
  const entries = useMemo(() => {
    const seen = new Set<string>();
    const list: PartTimelineEntry[] = [];
    for (const e of q.data ?? []) {
      const k = `${e.ecn_id}#${e.line_no}`;
      if (seen.has(k)) continue;
      seen.add(k);
      list.push(e);
    }
    return list;
  }, [q.data]);
  const specs = specQ.data ?? [];

  return (
    <Drawer
      title={<span>变更时间线 {partNo && <Tag color="blue">{partNo}</Tag>}</span>}
      open={!!partNo}
      onClose={onClose}
      width={620}
    >
      <Segmented
        value={view}
        onChange={(v) => setView(v as 'ecn' | 'spec')}
        options={[
          { label: `ECN 变更（${entries.length}）`, value: 'ecn' },
          { label: `规格书更新（${specs.length}）`, value: 'spec' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {view === 'ecn' ? (
        q.isLoading ? (
          <Spin />
        ) : entries.length === 0 ? (
          <Empty description="该物料暂无 ECN 变更记录" />
        ) : (
          <Timeline
            items={entries.map((e) => {
              const b = (e.before ?? {}) as EcnChangeItem;
              const a = (e.after ?? {}) as EcnChangeItem;
              const diffs = diffFields(b, a);
              return {
                color: 'blue',
                children: (
                  <div
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      padding: '10px 14px',
                      background: '#fafafa',
                      marginBottom: 4,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: diffs.length ? 10 : 0,
                      }}
                    >
                      <Space>
                        <Text strong>{fmtDate(e.ecn_date)}</Text>
                        <Button
                          size="small"
                          type="link"
                          icon={<DownloadOutlined />}
                          onClick={() =>
                            openEcnFile(tid, e.ecn_id).catch(() =>
                              message.error('该 ECN 无原文件或下载失败'),
                            )
                          }
                        >
                          下载 ECN 存档
                        </Button>
                      </Space>
                      <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                        {e.remark || '变更'}
                      </Tag>
                    </div>

                    {diffs.map((d) => (
                      <div
                        key={d.label}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '40px 1fr',
                          gap: '0 10px',
                          marginBottom: 8,
                        }}
                      >
                        <Text type="secondary" style={{ fontSize: 12, lineHeight: '22px' }}>
                          {d.label}
                        </Text>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {d.before && (
                            <span
                              style={{
                                ...pillBase,
                                background: '#fff1f0',
                                color: '#cf1322',
                                textDecoration: 'line-through',
                              }}
                            >
                              {d.before}
                            </span>
                          )}
                          <span style={{ ...pillBase, background: '#f6ffed', color: '#389e0d' }}>
                            {d.after || '—'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ),
              };
            })}
          />
        )
      ) : specQ.isLoading ? (
        <Spin />
      ) : specs.length === 0 ? (
        <Empty description="该物料暂无规格书" />
      ) : (
        <Timeline
          items={(specs as SpecDocMeta[]).map((s) => ({
            color: 'green',
            children: (
              <div
                style={{
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  padding: '10px 14px',
                  background: '#fafafa',
                  marginBottom: 4,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <Text strong style={{ wordBreak: 'break-all' }}>
                    {s.filename}
                  </Text>
                  <Button
                    size="small"
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() =>
                      openSpecFile(tid, s.id, s.part_no).catch(() => message.error('下载失败'))
                    }
                  >
                    下载
                  </Button>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {fmtTime(s.uploaded_at)}
                    {s.source_tenant_name ? ` ｜ 来源：${s.source_tenant_name}` : ''}
                    {(s.part_nos?.length ?? 0) > 1
                      ? ` ｜ 同组料号：${(s.part_nos ?? []).filter((pn) => pn !== s.part_no).join('、')}`
                      : ''}
                    {s.note ? ` ｜ ${s.note}` : ''}
                  </Text>
                </div>
              </div>
            ),
          }))}
        />
      )}
    </Drawer>
  );
}
